import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, withModifiers as _withModifiers, withKeys as _withKeys, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "file-input" }
const _hoisted_2 = ["contenteditable", "onKeydown", "onKeyup"]
const _hoisted_3 = {
  key: 0,
  class: "file-input__icon"
}

import { i18n } from "@/i18n";
import { ref, watch, computed, onMounted, nextTick } from "vue";
import AppIcon from "../ui-kit/icon/AppIcon.vue";
import AppTooltip from "../ui-kit/tooltip/AppTooltip.vue";

interface Props {
  modelValue: string;
  type?: string;
  newEl?: boolean;
  disabled?: boolean;
  blocked?: boolean;
  invalidValues?: string[];
  showInvalidState?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'FileInput',
  props: {
    modelValue: {},
    type: { default: "folder" },
    newEl: { type: Boolean },
    disabled: { type: Boolean, default: false },
    blocked: { type: Boolean, default: false },
    invalidValues: { default: () => [] },
    showInvalidState: { type: Boolean, default: true }
  },
  emits: ["update:modelValue", "clear", "enter", "blur"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;

const emit = __emit;

const originalText = ref(props.modelValue || "");
const innerText = ref(props.modelValue || "");
const editable = ref<HTMLElement | null>(null);
const isFocused = ref(false);
const isContentEditable = ref(false);
const isEditing = ref(false);
const isEnter = ref(false);

const normalizeComparedValue = (value: string) => {
  const normalized = value.trim().toLowerCase();
  return props.type === "file" ? removeExtension(normalized) : normalized;
};

const normalizedValue = computed(() => normalizeComparedValue(innerText.value));
const isDuplicate = computed(() =>
  props.invalidValues.some(
    (value) => normalizeComparedValue(value) === normalizedValue.value,
  ),
);
const isValid = computed(
  () => normalizedValue.value.length > 0 && !isDuplicate.value,
);
const invalidHint = computed(() =>
  isDuplicate.value
    ? String(i18n.global.t('element_name_exists'))
    : String(i18n.global.t('field_required')),
);

const displayText = computed(() => {
  if (isEditing.value && props.type === "file") {
    return removeExtension(innerText.value);
  }
  return innerText.value;
});

const onClick = (e: MouseEvent) => {
  if (isContentEditable.value) {
    e.stopPropagation();
  }
};

function removeExtension(filename: string): string {
  if (!filename) return "";
  const lastDotIndex = filename.lastIndexOf(".");
  if (lastDotIndex === -1) return filename;
  return filename.substring(0, lastDotIndex);
}

function getExtension(filename: string): string {
  if (!filename) return "";
  const lastDotIndex = filename.lastIndexOf(".");
  if (lastDotIndex === -1) return "";
  return filename.substring(lastDotIndex);
}

const clear = (e: MouseEvent) => {
  e.stopPropagation();

  innerText.value = "";

  updateEditable();

  nextTick(() => {
    if (editable.value) {
      focus();
    }
  });
};

const onPressEnter = () => {
  if (isValid.value) {
    saveChanges();
  }
};

const onPressEsc = () => {
  cancelEditing();
};

const onBlur = () => {
  if (!isEditing.value) {
    emit("blur");
    return;
  }

  if (isValid.value) {
    saveChanges();
    emit("blur");
    return;
  }

  nextTick(() => {
    editable.value?.focus();
    isFocused.value = true;
    isContentEditable.value = true;
  });
};

const saveChanges = () => {
  isEditing.value = false;

  if (originalText.value && props.type === "file") {
    const extension = getExtension(originalText.value);
    const currentText = editable.value?.innerText || innerText.value;

    const hasNewExtension =
      currentText.includes(".") && getExtension(currentText);
    if (!hasNewExtension && extension) {
      innerText.value = currentText + extension;
    } else {
      innerText.value = currentText;
    }
  }

  emit("update:modelValue", innerText.value);
  emit("enter", innerText.value);
  blur();
};

const cancelEditing = () => {
  isEditing.value = false;
  innerText.value = props.modelValue;

  updateEditable();
  blur();
};

const focus = () => {
  if (props.disabled) return;

  isContentEditable.value = true;
  isEditing.value = true;

  nextTick(() => {
    if (editable.value) {
      editable.value.focus();
      isFocused.value = true;

      const range = document.createRange();
      const selection = window.getSelection();
      range.selectNodeContents(editable.value);
      range.collapse(false);
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  });
};

const blur = () => {
  if (editable.value) {
    editable.value.blur();
    isFocused.value = false;
    isContentEditable.value = false;
  }
};

__expose({
  focus,
  blur,
  finishEditing: onBlur,
});

watch(
  () => props.modelValue,
  (v) => {
    if (v !== innerText.value) {
      innerText.value = v || "";
      originalText.value = v || "";
      updateEditable();
    }
  },
);

watch(innerText, (v) => {
  if (!isEditing.value) {
    emit("update:modelValue", v);
  }
});

function setEditableText(text: string) {
  if (!editable.value) return;

  if (isEditing.value) {
    editable.value.innerText = removeExtension(text);
  } else {
    editable.value.innerText = text;
  }
}

function updateEditable() {
  setEditableText(innerText.value);
}

function onInput(e: Event) {
  const el = editable.value;
  if (!el) return;

  const textWithoutExtension = el.innerText || "";
  innerText.value = textWithoutExtension;

  const caret = saveCaretPosition(el);

  nextTick(() => {
    restoreCaretPosition(el, caret);
  });
}

function saveCaretPosition(el: HTMLElement) {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return null;

  const range = selection.getRangeAt(0);
  const preRange = range.cloneRange();

  preRange.selectNodeContents(el);
  preRange.setEnd(range.endContainer, range.endOffset);

  return preRange.toString().length;
}

function restoreCaretPosition(el: HTMLElement, caretPos: number) {
  if (caretPos === null) return;

  const nodeStack = [el];
  let charCount = 0;
  let node: Node | undefined;

  while ((node = nodeStack.pop())) {
    if (node.nodeType === Node.TEXT_NODE) {
      const textLength = (node.textContent || "").length;

      if (charCount + textLength >= caretPos) {
        const selection = window.getSelection();
        const range = document.createRange();
        range.setStart(node, caretPos - charCount);
        range.collapse(true);
        selection?.removeAllRanges();
        selection?.addRange(range);
        return;
      }
      charCount += textLength;
    } else {
      let i = node.childNodes.length;
      while (i--) nodeStack.push(node.childNodes[i]);
    }
  }
}

onMounted(() => {
  originalText.value = props.modelValue || "";
  updateEditable();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("span", _hoisted_1, [
    _createElementVNode("div", {
      ref_key: "editable",
      ref: editable,
      class: _normalizeClass(["file-input__text-field", [
        `file-input-${_ctx.type}`,
        { 'file-input__text-field--invalid': _ctx.showInvalidState && !isValid.value },
        { 'file-input__text-field--disabled': _ctx.disabled || _ctx.blocked },
      ]]),
      contenteditable: 
        !_ctx.disabled && (isValid.value && !_ctx.newEl ? isContentEditable.value : true)
      ,
      onFocus: focus,
      onBlur: onBlur,
      onKeydown: [
        _withKeys(_withModifiers(onPressEnter, ["prevent"]), ["enter"]),
        _withKeys(onPressEsc, ["esc"])
      ],
      onKeyup: _withKeys(_withModifiers(clear, ["ctrl"]), ["backspace"]),
      onInput: onInput,
      onClick: onClick
    }, _toDisplayString(displayText.value), 43, _hoisted_2),
    (_ctx.showInvalidState && !isValid.value)
      ? (_openBlock(), _createBlock(AppTooltip, {
          key: 0,
          text: invalidHint.value,
          position: "top"
        }, {
          activator: _withCtx(() => [
            (!isValid.value)
              ? (_openBlock(), _createElementBlock("span", _hoisted_3, [
                  _createVNode(AppIcon, {
                    name: "invalid",
                    width: "16px",
                    color: "#FF5533",
                    cursor: "pointer"
                  })
                ]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }, 8, ["text"]))
      : _createCommentVNode("", true),
    (isFocused.value && normalizedValue.value.length > 0)
      ? (_openBlock(), _createBlock(AppTooltip, {
          key: 1,
          text: _ctx.$t('clear_field_hotkey')
        }, {
          activator: _withCtx(() => [
            _createElementVNode("span", {
              onClick: clear,
              onMousedown: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["prevent"]))
            }, [
              _createVNode(AppIcon, {
                name: "arrow-left",
                width: "16px",
                color: "#979797",
                cursor: "pointer"
              })
            ], 32)
          ]),
          _: 1
        }, 8, ["text"]))
      : _createCommentVNode("", true)
  ]))
}
}

})